import type { Command } from "@/commands/Command";
import {
  UpdateFlexChildLayoutCommand,
  UpdateFrameClipsContentCommand,
  UpdateFrameCornerRadiiCommand,
  UpdateFrameLayoutCommand,
  UpdateNodeBoundsCommand,
  UpdateNodeConstraintsCommand,
  UpdateNodeStyleCommand,
  UpdatePrimitiveDefinitionCommand,
  UpdateTextCommand,
  type FrameLayoutSettings,
} from "@/commands/nodeCommands";
import {
  CreateDocumentScriptCommand,
  CreateScriptFolderCommand,
  DeleteScriptEntryCommand,
  DuplicateDocumentScriptCommand,
  MoveScriptEntryCommand,
  RenameScriptEntryCommand,
  SetFrameScriptIdCommand,
  UpdateDocumentScriptSourceCommand,
} from "@/commands/scriptLibraryCommands";
import {
  ApplyDocumentStyleCommand,
  CreateDocumentStyleCommand,
  DeleteDocumentStyleCommand,
  DetachDocumentStyleCommand,
  UpdateDocumentStyleCommand,
} from "@/commands/styleCommands";
import {
  createDocumentScript,
  createScriptFolder,
  findDocumentScript,
  findScriptEntry,
  uniqueFolderName,
  uniqueScriptName,
} from "@/document/model/documentScripts";
import { TransactionCommand } from "@/commands/transaction";
import { getAllSceneNodes } from "@/document/model/document";
import type { CreateStyleFromSelectionOptions, DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import { inlineStyleBinding, styleBindingId } from "@/document/model/documentStyles";
import {
  createDefaultFrameFlexLayout,
  createDefaultFrameMirrorLayout,
  createDefaultFrameRepeatLayout,
  flexChildLayout,
  frameAutoLayoutMode,
  frameCornerRadii,
  frameFlexLayout,
  frameMirrorLayout,
  frameRepeatLayout,
  nodeConstraints,
  type AutoLayoutMode,
  type FlexChildLayout,
  type FrameFlexLayout,
  type FrameMirrorLayout,
  type FrameRepeatLayout,
  type NodeConstraints,
  type SceneNode,
} from "@/document/model/nodes";
import type { PrimitiveDefinition } from "@/document/model/primitives";
import type { NodeStyle, Paint } from "@/document/model/styles";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { documentStore } from "@/editor/store";
import type { Rect } from "@/lib/math/rect";
import { styleWithPaint } from "../../shell/right-panel/paintStyles";
import {
  defaultSelectionPaint,
  nodeWithSelectionPaint,
  topSelectionPaint,
  type SelectionPaintKind,
} from "../../shell/right-panel/selectionPaintPicker";
import type { PaintTarget } from "../../shell/right-panel/types";
import { boundsEqual, primitiveEqual, styleEqual, textEqual } from "../../shell/shellSelection";
import type { EditorCommandHost } from "../editorCommandHost";

export function updateNodeStyle(host: EditorCommandHost, nodeId: string, style: NodeStyle): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || isNodeLockedForEditing(document, node.id) || styleEqual(node.style, style)) {
    return;
  }

  host.runDocumentCommands([new UpdateNodeStyleCommand(nodeId, node.style, style)], "Updated style");
}

export function updateSelectedNodeStyles(
  host: EditorCommandHost,
  updates: Array<{ nodeId: string; style: NodeStyle }>,
  label: string,
): void {
  if (updates.length === 0) {
    return;
  }

  const document = documentStore.getDocument();
  const updateById = new Map(updates.map(update => [update.nodeId, update.style]));
  const commands = getAllSceneNodes(document).flatMap(node => {
    const style = updateById.get(node.id);
    if (!style || isNodeLockedForEditing(document, node.id) || styleEqual(node.style, style)) {
      return [];
    }
    return [new UpdateNodeStyleCommand(node.id, node.style, style)];
  });

  host.runDocumentCommands(commands, label);
}

export function ensureSelectionPaint(
  host: EditorCommandHost,
  nodeIds: string[],
  kind: SelectionPaintKind,
): string[] {
  const document = documentStore.getDocument();
  const selectedIds = new Set(nodeIds);
  const editableNodes = getAllSceneNodes(document).filter(
    node => selectedIds.has(node.id) && !isNodeLockedForEditing(document, node.id),
  );
  const emptyNodeIds = editableNodes
    .filter(node => !topSelectionPaint(document, node, kind))
    .map(node => node.id);

  if (emptyNodeIds.length > 0) {
    updateSelectionPaint(host, emptyNodeIds, kind, defaultSelectionPaint(kind), `Added ${kind}`);
  }
  return editableNodes.map(node => node.id);
}

export function updateSelectionPaint(
  host: EditorCommandHost,
  nodeIds: string[],
  kind: SelectionPaintKind,
  paint: Paint,
  label = `Updated ${kind}`,
): void {
  const document = documentStore.getDocument();
  const selectedIds = new Set(nodeIds);
  const commands = getAllSceneNodes(document).flatMap(node => {
    if (!selectedIds.has(node.id) || isNodeLockedForEditing(document, node.id)) {
      return [];
    }

    const hadBinding = Boolean(styleBindingId(node, kind));
    const localNode = hadBinding ? inlineStyleBinding(document, node, kind) : node;
    const nextNode = nodeWithSelectionPaint(document, node, kind, paint);
    const nodeCommands: Command[] = hadBinding
      ? [new DetachDocumentStyleCommand(node.id, kind)]
      : [];

    if (kind === "fill" && localNode.type === "text" && nextNode.type === "text") {
      if (!textEqual(localNode.text, nextNode.text)) {
        nodeCommands.push(new UpdateTextCommand(node.id, localNode.text, nextNode.text));
      }
    } else if (!styleEqual(localNode.style, nextNode.style)) {
      nodeCommands.push(new UpdateNodeStyleCommand(node.id, localNode.style, nextNode.style));
    }
    return nodeCommands;
  });

  host.runDocumentCommands(commands, label);
}

export function updatePrimitiveDefinition(
  host: EditorCommandHost,
  nodeId: string,
  primitive: PrimitiveDefinition,
): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "primitive" || isNodeLockedForEditing(document, node.id) || primitiveEqual(node.primitive, primitive)) {
    return;
  }

  host.runDocumentCommands([new UpdatePrimitiveDefinitionCommand(nodeId, node.primitive, primitive)], "Updated primitive");
}

export function updateGradientPaint(host: EditorCommandHost, nodeId: string, target: PaintTarget, paint: Paint): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || isNodeLockedForEditing(document, node.id)) {
    return;
  }

  updateNodeStyle(host, nodeId, styleWithPaint(node.style, target, paint));
}

export function updateFrameClipsContent(host: EditorCommandHost, nodeId: string, clipsContent: boolean): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "frame" || node.clipsContent === clipsContent) {
    return;
  }

  host.runDocumentCommands(
    [new UpdateFrameClipsContentCommand(nodeId, node.clipsContent, clipsContent)],
    clipsContent ? "Enabled frame clipping" : "Disabled frame clipping",
  );
}

export function createDocumentScriptEntry(
  host: EditorCommandHost,
  name?: string,
  parentFolderId?: string,
): string {
  const document = documentStore.getDocument();
  const library = document.scripts ?? { items: [] };
  const script = createDocumentScript(uniqueScriptName(library, name ?? "Script"));
  host.runDocumentCommands(
    [new CreateDocumentScriptCommand(script, parentFolderId)],
    "Created script",
  );
  return script.id;
}

export function createScriptFolderEntry(host: EditorCommandHost, name?: string): string {
  const document = documentStore.getDocument();
  const library = document.scripts ?? { items: [] };
  const folder = createScriptFolder(uniqueFolderName(library, name ?? "Folder"));
  host.runDocumentCommands([new CreateScriptFolderCommand(folder)], "Created script folder");
  return folder.id;
}

export function renameScriptEntry(host: EditorCommandHost, entryId: string, name: string): void {
  const entry = findScriptEntry(documentStore.getDocument().scripts, entryId);
  const trimmed = name.trim();
  if (!entry || !trimmed || entry.name === trimmed) {
    return;
  }
  host.runDocumentCommands(
    [new RenameScriptEntryCommand(entryId, entry.name, trimmed)],
    entry.type === "folder" ? "Renamed script folder" : "Renamed script",
  );
}

export function updateDocumentScriptSource(host: EditorCommandHost, scriptId: string, source: string): void {
  const script = findDocumentScript(documentStore.getDocument().scripts, scriptId);
  if (!script || script.source === source) {
    return;
  }
  host.runDocumentCommands(
    [new UpdateDocumentScriptSourceCommand(scriptId, script.source, source)],
    "Edited script",
  );
}

export function duplicateDocumentScript(host: EditorCommandHost, scriptId: string): string | undefined {
  const command = new DuplicateDocumentScriptCommand(scriptId);
  host.runDocumentCommands([command], "Duplicated script");
  return command.getCreatedScriptId();
}

export function deleteScriptEntry(host: EditorCommandHost, entryId: string): void {
  if (!findScriptEntry(documentStore.getDocument().scripts, entryId)) {
    return;
  }
  const entry = findScriptEntry(documentStore.getDocument().scripts, entryId);
  host.runDocumentCommands(
    [new DeleteScriptEntryCommand(entryId)],
    entry?.type === "folder" ? "Deleted script folder" : "Deleted script",
  );
}

export function moveScriptEntry(
  host: EditorCommandHost,
  entryId: string,
  toParentFolderId: string | undefined,
  toIndex: number,
): void {
  host.runDocumentCommands(
    [new MoveScriptEntryCommand(entryId, toParentFolderId, toIndex)],
    "Moved script",
  );
}

export function setFrameScriptId(host: EditorCommandHost, frameId: string, scriptId: string | undefined): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === frameId);
  if (node?.type !== "frame") {
    return;
  }
  if (node.scriptId === scriptId) {
    return;
  }
  host.runDocumentCommands(
    [new SetFrameScriptIdCommand(frameId, node.scriptId, scriptId)],
    scriptId === undefined ? "Detached script" : node.scriptId === undefined ? "Attached script" : "Changed script",
  );
}

/** @deprecated Prefer renameScriptEntry */
export function renameDocumentScript(host: EditorCommandHost, scriptId: string, name: string): void {
  renameScriptEntry(host, scriptId, name);
}

/** @deprecated Prefer deleteScriptEntry */
export function deleteDocumentScript(host: EditorCommandHost, scriptId: string): void {
  deleteScriptEntry(host, scriptId);
}

export function updateFrameCornerRadii(
  host: EditorCommandHost,
  nodeId: string,
  cornerRadii: [number, number, number, number],
): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "frame" || isNodeLockedForEditing(document, node.id)) {
    return;
  }

  const fromRadii = frameCornerRadii(node);
  const maxRadius = Math.min(Math.abs(node.bounds.width), Math.abs(node.bounds.height)) / 2;
  const toRadii = cornerRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number];
  if (fromRadii.every((radius, index) => radius === toRadii[index])) {
    return;
  }

  host.runDocumentCommands([new UpdateFrameCornerRadiiCommand(nodeId, fromRadii, toRadii)], "Updated frame corners");
}

export function updateFrameLayout(
  host: EditorCommandHost,
  nodeId: string,
  patch: {
    autoLayout?: AutoLayoutMode;
    flexLayout?: FrameFlexLayout;
    repeatLayout?: FrameRepeatLayout;
    mirrorLayout?: FrameMirrorLayout;
  },
): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "frame" || isNodeLockedForEditing(document, node.id)) {
    return;
  }

  const fromLayout = frameLayoutSettings(node);
  const toLayout: FrameLayoutSettings = {
    autoLayout: patch.autoLayout ?? fromLayout.autoLayout,
    flexLayout: patch.flexLayout ?? fromLayout.flexLayout,
    repeatLayout: patch.repeatLayout ?? fromLayout.repeatLayout,
    mirrorLayout: patch.mirrorLayout ?? fromLayout.mirrorLayout,
  };
  if (frameLayoutSettingsEqual(fromLayout, toLayout)) {
    return;
  }

  const label = toLayout.autoLayout === "flex"
    ? "Updated flex layout"
    : toLayout.autoLayout === "repeat"
      ? "Updated repeat layout"
      : toLayout.autoLayout === "mirror"
        ? "Updated mirror layout"
        : "Updated layout";
  host.runDocumentCommands(
    [new UpdateFrameLayoutCommand(nodeId, fromLayout, toLayout)],
    label,
  );
}

export function updateFlexChildLayout(host: EditorCommandHost, nodeId: string, layout: FlexChildLayout): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || isNodeLockedForEditing(document, node.id)) {
    return;
  }

  const currentLayout = flexChildLayout(node);
  const nextLayout = flexChildLayoutWithStoredSize(currentLayout, layout, node.bounds);
  const restoredBounds = restoredFlexChildBounds(node.bounds, currentLayout, nextLayout);
  if (flexChildLayoutEqual(currentLayout, nextLayout) && boundsEqual(node.bounds, restoredBounds)) {
    return;
  }

  const commands: Command[] = [
    new UpdateFlexChildLayoutCommand(nodeId, node.flexChild, nextLayout),
    ...(boundsEqual(node.bounds, restoredBounds) ? [] : [new UpdateNodeBoundsCommand(nodeId, node.bounds, restoredBounds)]),
  ];
  host.runDocumentCommands(commands, "Updated flex child");
}

export function updateNodeConstraints(host: EditorCommandHost, nodeId: string, constraints: NodeConstraints): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || isNodeLockedForEditing(document, node.id)) {
    return;
  }

  const current = nodeConstraints(node);
  if (current.horizontal === constraints.horizontal && current.vertical === constraints.vertical) {
    return;
  }

  host.runDocumentCommands([new UpdateNodeConstraintsCommand(nodeId, node.constraints, constraints)], "Updated constraints");
}

export function createDocumentStyle(
  host: EditorCommandHost,
  nodeId: string,
  kind: DocumentStyleKind,
  name: string,
  options: CreateStyleFromSelectionOptions = {},
): string | undefined {
  const context = host.commandContext();
  const command = new CreateDocumentStyleCommand(nodeId, kind, name, options);
  host.history.execute(command, context, { afterSelection: host.currentSelection() });
  host.commit(context.document, `Created ${kind} style`);
  return command.createdStyleId;
}

export function applyDocumentStyle(host: EditorCommandHost, nodeId: string, styleId: string): void {
  host.runDocumentCommands([new ApplyDocumentStyleCommand(nodeId, styleId)], "Applied style");
}

export function detachDocumentStyle(host: EditorCommandHost, nodeId: string, kind: DocumentStyleKind): void {
  host.runDocumentCommands([new DetachDocumentStyleCommand(nodeId, kind)], "Detached style");
}

export function updateDocumentStyle(host: EditorCommandHost, style: DocumentStyle): void {
  host.runDocumentCommands([new UpdateDocumentStyleCommand(style.id, style)], "Updated style");
}

export function deleteDocumentStyle(host: EditorCommandHost, styleId: string): void {
  host.runDocumentCommands([new DeleteDocumentStyleCommand(styleId)], "Deleted style");
}

function frameLayoutSettings(node: Extract<SceneNode, { type: "frame" }>): FrameLayoutSettings {
  return {
    autoLayout: frameAutoLayoutMode(node),
    flexLayout: node.flexLayout ? structuredClone(frameFlexLayout(node)) : createDefaultFrameFlexLayout(),
    repeatLayout: node.repeatLayout ? structuredClone(frameRepeatLayout(node)) : createDefaultFrameRepeatLayout(),
    mirrorLayout: node.mirrorLayout ? structuredClone(frameMirrorLayout(node)) : createDefaultFrameMirrorLayout(),
  };
}

function frameLayoutSettingsEqual(left: FrameLayoutSettings, right: FrameLayoutSettings): boolean {
  return left.autoLayout === right.autoLayout
    && left.flexLayout.direction === right.flexLayout.direction
    && left.flexLayout.justifyContent === right.flexLayout.justifyContent
    && left.flexLayout.alignItems === right.flexLayout.alignItems
    && left.flexLayout.gap === right.flexLayout.gap
    && left.flexLayout.padding.top === right.flexLayout.padding.top
    && left.flexLayout.padding.right === right.flexLayout.padding.right
    && left.flexLayout.padding.bottom === right.flexLayout.padding.bottom
    && left.flexLayout.padding.left === right.flexLayout.padding.left
    && left.flexLayout.widthSizing === right.flexLayout.widthSizing
    && left.flexLayout.heightSizing === right.flexLayout.heightSizing
    && left.repeatLayout.mode === right.repeatLayout.mode
    && left.repeatLayout.count === right.repeatLayout.count
    && left.repeatLayout.columns === right.repeatLayout.columns
    && left.repeatLayout.rows === right.repeatLayout.rows
    && left.repeatLayout.gap === right.repeatLayout.gap
    && left.repeatLayout.columnGap === right.repeatLayout.columnGap
    && left.repeatLayout.rowGap === right.repeatLayout.rowGap
    && left.repeatLayout.radius === right.repeatLayout.radius
    && left.repeatLayout.startAngle === right.repeatLayout.startAngle
    && left.mirrorLayout.axis === right.mirrorLayout.axis
    && left.mirrorLayout.gap === right.mirrorLayout.gap;
}

function flexChildLayoutEqual(left: FlexChildLayout, right: FlexChildLayout): boolean {
  return left.grow === right.grow
    && left.shrink === right.shrink
    && left.alignSelf === right.alignSelf
    && left.absolute === right.absolute
    && left.fillWidth === right.fillWidth
    && left.fillHeight === right.fillHeight
    && left.fixedWidth === right.fixedWidth
    && left.fixedHeight === right.fixedHeight;
}

function flexChildLayoutWithStoredSize(current: FlexChildLayout, next: FlexChildLayout, bounds: Rect): FlexChildLayout {
  return {
    ...next,
    fixedWidth: !current.fillWidth && next.fillWidth ? bounds.width : next.fixedWidth ?? current.fixedWidth,
    fixedHeight: !current.fillHeight && next.fillHeight ? bounds.height : next.fixedHeight ?? current.fixedHeight,
  };
}

function restoredFlexChildBounds(bounds: Rect, current: FlexChildLayout, next: FlexChildLayout): Rect {
  return {
    ...bounds,
    width: current.fillWidth && !next.fillWidth && Number.isFinite(current.fixedWidth)
      ? Math.max(0, current.fixedWidth!)
      : bounds.width,
    height: current.fillHeight && !next.fillHeight && Number.isFinite(current.fixedHeight)
      ? Math.max(0, current.fixedHeight!)
      : bounds.height,
  };
}
